/* eslint-disable camelcase */
import { getCurrentConfig } from '@swarm/core/observables/configForNetwork'
import { unifyAddressToId, useAccount } from '@swarm/core/web3'
import { DotcAsset } from '@swarm/types/tokens/dotc'
import { XOfferFilter } from '@swarm/types/x-subgraph/graphql'
import { getUnixTime } from 'date-fns'
import { useMemo, useRef } from 'react'
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params'

interface UseGoldOffersOptionsArgs {
  tokenIn?: DotcAsset
  tokenOut?: DotcAsset
  isPrivateOffers?: boolean
}

const { xGoldBundleAddress, xGoldKgAddress, xGoldOzAddress } =
  getCurrentConfig()

const useGoldOffersOptions = ({
  tokenIn,
  tokenOut,
  isPrivateOffers = false,
}: UseGoldOffersOptionsArgs) => {
  const account = useAccount()
  const [queryParams] = useQueryParams({
    id: StringParam,
    tokenIn: StringParam,
    tokenOut: StringParam,
    showPrivate: BooleanParam,
  })
  const { current: currentTimestamp } = useRef(getUnixTime(Date.now()))

  const commonFilter = useMemo(
    () => ({
      ...(queryParams.id && {
        id: queryParams.id,
      }),
      isCompleted: false,
      cancelled: false,
      expiresAt_gt: currentTimestamp.toString(),
      isPrivate: isPrivateOffers,
      ...(isPrivateOffers &&
        account && {
          specialAddresses_contains_nocase: [account],
        }),
    }),
    [account, currentTimestamp, isPrivateOffers, queryParams.id],
  )

  const filter = useMemo<XOfferFilter>(() => {
    const goldAddresses = [
      unifyAddressToId(xGoldKgAddress),
      unifyAddressToId(xGoldOzAddress),
      unifyAddressToId(xGoldBundleAddress),
    ]
    const tokenInIsGold =
      !!tokenIn?.address && goldAddresses.includes(tokenIn.address)
    const tokenOutIsGold =
      !!tokenOut?.address && goldAddresses.includes(tokenOut.address)

    const getDepositAssetFilter = () => {
      if (tokenIn?.address && tokenOut?.address) {
        return {
          depositAsset_: {
            address: unifyAddressToId(tokenIn.address),
          },
        }
      }

      const onlyTokenOutIsGold = tokenOutIsGold && !tokenIn?.address

      if (onlyTokenOutIsGold) {
        return {}
      }

      return {
        depositAsset_: {
          address_in: tokenIn?.address
            ? [unifyAddressToId(tokenIn.address)]
            : goldAddresses,
        },
      }
    }

    const getWithdrawalAssetFilter = () => {
      if (tokenIn?.address && tokenOut?.address) {
        return {
          withdrawalAsset_: {
            address: unifyAddressToId(tokenOut.address),
          },
        }
      }

      const onlyTokenInIsGold = tokenInIsGold && !tokenOut?.address

      if (onlyTokenInIsGold) {
        return {}
      }

      return {
        withdrawalAsset_: {
          address_in: tokenOut?.address
            ? [unifyAddressToId(tokenOut.address)]
            : goldAddresses,
        },
      }
    }

    return {
      [tokenIn?.address || tokenOut?.address ? 'and' : 'or']: [
        {
          ...commonFilter,
          ...getDepositAssetFilter(),
        },
        {
          ...commonFilter,
          ...getWithdrawalAssetFilter(),
        },
      ],
    }
  }, [commonFilter, tokenIn?.address, tokenOut?.address])

  return { variables: { filter } }
}

export default useGoldOffersOptions
